export enum Platform {
  Instagram = 'Instagram',
  TikTok = 'TikTok',
  YouTube = 'YouTube',
  Facebook = 'Facebook',
  Pinterest = 'Pinterest',
  Email = 'Email',
}

export type SocialPlatform = Exclude<Platform, Platform.Email>;

export const SOCIAL_PLATFORMS = Object.values(Platform).filter(
  (platform): platform is Exclude<Platform, Platform.Email> =>
    platform !== Platform.Email
) satisfies SocialPlatform[];

export const CONNECTABLE_SOCIAL_PLATFORMS = SOCIAL_PLATFORMS.filter(
  (platform) => platform !== Platform.Pinterest
);

export const VETTABLE_PLATFORMS = Object.values(Platform).filter((platform) =>
  [Platform.Instagram, Platform.TikTok].includes(platform)
);

export const isVettablePlatform = (platform: Platform): boolean =>
  VETTABLE_PLATFORMS.includes(platform);

export function calculateVettablePlatformsCompletion(
  connectedPlatforms: Platform[],
  selectedPlatforms: Platform[]
): number {
  if (selectedPlatforms.length === 0) {
    return 0;
  }
  if (!selectedPlatforms.some((platform) => isVettablePlatform(platform))) {
    return 100;
  }

  const vettableConnected = connectedPlatforms.filter((platform) =>
    isVettablePlatform(platform)
  );
  const vettableSelected = selectedPlatforms.filter((platform) =>
    isVettablePlatform(platform)
  );

  const vettableSet = new Set([...vettableConnected, ...vettableSelected]);

  return vettableSelected.length
    ? (vettableConnected.length / vettableSet.size) * 100
    : 0;
}

// Social Network types based on server-side enum
export const SOCIAL_NETWORK_TYPES = [
  'instagram',
  'pinterest',
  'pinterest_organic',
  'youtube',
  'facebook',
  'instagram_business',
  'tiktok',
  'tiktok_creator_marketplace',
  'tiktok_v2',
  'twitter',
] as const;

export type SocialNetwork = (typeof SOCIAL_NETWORK_TYPES)[number];

export const isSocialNetwork = (network: string): network is SocialNetwork =>
  SOCIAL_NETWORK_TYPES.includes(network as SocialNetwork);
